import React, { useCallback, useEffect, useState } from 'react';
import { Search, RefreshCw, X, ClipboardList, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApi } from '../../lib/api';

const PAGE_SIZE = 25;

export default function AuditLogPage() {
  const api = useApi();
  const [filter, setFilter] = useState({ action: '', entity_type: '', app_id: '' });
  const [entries, setEntries] = useState([]);
  const [actions, setActions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, l] = await Promise.all([
        api.adminAuditActions(),
        api.adminAudit({
          action:      filter.action      || undefined,
          entity_type: filter.entity_type || undefined,
          app_id:      filter.app_id      || undefined,
          limit: 200,
        }),
      ]);
      setActions(a.actions || []);
      setEntries(l.entries || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = entries.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (e.action || '').toLowerCase().includes(q) ||
      (e.actor_email || '').toLowerCase().includes(q) ||
      (e.entity_type || '').toLowerCase().includes(q) ||
      (e.app_name || '').toLowerCase().includes(q) ||
      String(e.id).includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageNums = buildPageNums(page, totalPages);

  const selectStyle = {
    font: 'inherit', fontSize: 13, padding: '6px 28px 6px 10px',
    border: '1px solid #d1d5db', borderRadius: 8, appearance: 'none',
    background: `white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 8px center`,
    color: '#374151', cursor: 'pointer',
  };

  if (error) return <div className="admin-banner error">{error}</div>;

  return (
    <>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Audit log</h1>
          <p className="admin-page-subtitle">Plan changes, payment events, credit adjustments, finding resolutions.</p>
        </div>
        <div className="admin-header-actions">
          <button className="btn-secondary" onClick={load} disabled={loading} style={{ gap: 6 }}>
            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="admin-kpi-row">
        <div className="admin-kpi">
          <div className="admin-kpi-icon blue"><ClipboardList size={18} /></div>
          <p className="admin-kpi-label">Total entries</p>
          <p className="admin-kpi-value">{entries.length}</p>
          <p className="admin-kpi-sub">{filter.action || filter.entity_type || filter.app_id ? 'matching filters' : 'last 200'}</p>
        </div>
        <div className="admin-kpi">
          <div className="admin-kpi-icon purple"><ClipboardList size={18} /></div>
          <p className="admin-kpi-label">Unique actions</p>
          <p className="admin-kpi-value">{actions.length}</p>
          <p className="admin-kpi-sub">Distinct event types</p>
        </div>
        <div className="admin-kpi">
          <div className="admin-kpi-icon green"><ClipboardList size={18} /></div>
          <p className="admin-kpi-label">Showing</p>
          <p className="admin-kpi-value">{filtered.length}</p>
          <p className="admin-kpi-sub">After search filter</p>
        </div>
      </div>

      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
            <input
              placeholder="Search entries..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              autoComplete="off"
              style={{ font: 'inherit', fontSize: 13, padding: '6px 12px 6px 30px', border: '1px solid #d1d5db', borderRadius: 8, width: 220, outline: 'none', color: '#374151' }}
            />
          </div>

          <select
            value={filter.action}
            onChange={e => { setFilter({ ...filter, action: e.target.value }); setPage(1); }}
            style={selectStyle}
          >
            <option value="">All actions</option>
            {actions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          <input
            placeholder="Entity type..."
            value={filter.entity_type}
            onChange={e => { setFilter({ ...filter, entity_type: e.target.value }); setPage(1); }}
            style={{ font: 'inherit', fontSize: 13, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, width: 140, outline: 'none', color: '#374151' }}
          />

          <input
            placeholder="App ID..."
            value={filter.app_id}
            onChange={e => { setFilter({ ...filter, app_id: e.target.value }); setPage(1); }}
            style={{ font: 'inherit', fontSize: 13, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, width: 100, outline: 'none', color: '#374151' }}
          />

          {(filter.action || filter.entity_type || filter.app_id || search) && (
            <button
              onClick={() => { setFilter({ action: '', entity_type: '', app_id: '' }); setSearch(''); setPage(1); }}
              style={{ display: 'flex', alignItems: 'center', gap: 4, font: 'inherit', fontSize: 13, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px' }}
            >
              <X size={13} /> Clear filters
            </button>
          )}

          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9ca3af' }}>{filtered.length} entries</span>
        </div>

        {filtered.length === 0 ? (
          <div className="admin-empty">No entries match.</div>
        ) : (
          <div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>SL. NO</th>
                  <th>WHEN</th>
                  <th>ACTOR</th>
                  <th>ACTION</th>
                  <th>ENTITY</th>
                  <th>APP</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paged.map(e => (
                  <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(e)}>
                    <td style={{ color: '#9ca3af', fontSize: 12 }}>{e.id}</td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>{(e.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td style={{ fontSize: 13, color: '#374151' }}>{e.actor_email || 'system'}</td>
                    <td>
                      <code style={{ fontSize: 12, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, color: '#374151' }}>{e.action}</code>
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>
                      {e.entity_type}{e.entity_id ? ` #${e.entity_id}` : ''}
                    </td>
                    <td style={{ fontSize: 13, color: '#374151' }}>{e.app_name || (e.app_id ? `#${e.app_id}` : '—')}</td>
                    <td>
                      <button style={{ font: 'inherit', fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)} to {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} entries
              </span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtn(page === 1)}>
                  <ChevronLeft size={14} />
                </button>
                {pageNums.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: 13 }}>…</span>
                    : <button key={p} onClick={() => setPage(p)} style={pageBtn(false, page === p)}>{p}</button>
                )}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtn(page === totalPages)}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <div className="modal-panel modal-panel--lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Audit entry #{selected.id}</h2>
              <button className="modal-close" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Action</div>
                  <code style={{ fontSize: 13, background: '#f3f4f6', padding: '3px 8px', borderRadius: 4, color: '#374151' }}>{selected.action}</code>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Actor</div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{selected.actor_email || 'system'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Entity</div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{selected.entity_type}{selected.entity_id ? ` #${selected.entity_id}` : ''}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>When</div>
                  <div style={{ fontSize: 13, color: '#374151' }}>
                    {(selected.created_at || '').slice(0, 19).replace('T', ' ')}
                    {selected.ip_address && <span style={{ color: '#9ca3af' }}> · {selected.ip_address}</span>}
                  </div>
                </div>
                {selected.app_name && (
                  <div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>App</div>
                    <div style={{ fontSize: 13, color: '#374151' }}>{selected.app_name}</div>
                  </div>
                )}
              </div>

              {selected.old_value_json && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, marginTop: 16 }}>Old value</div>
                  <pre style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '12px', borderRadius: 8, fontSize: 12, overflow: 'auto', maxHeight: 200, color: '#374151', margin: 0 }}>
                    {pretty(selected.old_value_json)}
                  </pre>
                </>
              )}
              {selected.new_value_json && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, marginTop: 16 }}>New value</div>
                  <pre style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '12px', borderRadius: 8, fontSize: 12, overflow: 'auto', maxHeight: 200, color: '#374151', margin: 0 }}>
                    {pretty(selected.new_value_json)}
                  </pre>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </>
  );
}

function pretty(s) {
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
}

function buildPageNums(cur, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1, 2, 3];
  if (cur > 3 && cur < total - 1) { pages.push('…', cur); }
  pages.push('…', total);
  return [...new Set(pages)];
}

function pageBtn(disabled, active) {
  return {
    width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6,
    background: active ? '#0f1c2e' : 'white',
    color: disabled ? '#d1d5db' : active ? 'white' : '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, fontWeight: active ? 700 : 400,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}
